import { FORCE_POLLING, POLLING } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { MobXProviderContext, observer } from 'mobx-react';
import { useCallLogs } from 'Modules/callLogs/index.request';
import React, { useCallback, useEffect, useRef } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { retryWithBackoff } from 'Utils/exponentialBackoff';
import { getLogger } from 'Utils/logger';
import { randomNumberBetween } from 'Utils/random';
import { isNullOrUndefined } from 'Utils/typeGuards';
import useConversationsPollingV2 from './hooks/useConversationsPollingV2';
import useMessagesPolling from './hooks/useMessagesPolling';
import usePresencesPolling from './hooks/usePresencesPolling';
import {
  FORCE_POLLING_INTERVAL_MS,
  HEARTBEAT_INTERVAL_MS,
  NETWORK_STABILIZATION_DELAY_MS,
  SLEEP_DETECTION_THRESHOLD_MS,
} from './index.constant';
import { ForcePollingFeatureFlags, PollingFeatureFlags } from './index.types';

/**
 * Global flag to suppress error notifications during retry attempts.
 * This prevents duplicate error banners while retries are in progress.
 */
let suppressErrorNotifications = false;
let networkRecoveryTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Checks if error notifications should be suppressed
 */
export const shouldSuppressErrorNotification = () => suppressErrorNotifications;

/**
 * Triggers network recovery mode when ERR_NETWORK_CHANGED is detected.
 * Suppresses error notifications for a grace period to allow network to stabilize.
 */
export const triggerNetworkRecovery = (reason: string) => {
  const logger = getLogger();
  logger.info('PollingProvider: Network recovery triggered', { reason });

  if (networkRecoveryTimeout) {
    clearTimeout(networkRecoveryTimeout);
  }

  suppressErrorNotifications = true;

  networkRecoveryTimeout = setTimeout(() => {
    suppressErrorNotifications = false;
    networkRecoveryTimeout = null;
  }, 10000);
};

const PollingProvider = ({ children }: React.PropsWithChildren) => {
  const { [POLLING]: pollingConfigs } = useFeatureFlags<PollingFeatureFlags>([
    POLLING,
  ]);

  const { [FORCE_POLLING]: forcePolling } =
    useFeatureFlags<ForcePollingFeatureFlags>([FORCE_POLLING]);

  const {
    pusher: { isOnline },
    person: personStore,
    callLogsStore: { searchQuery },
    participant: participantStore,
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const isLoggedIn = personStore.IsLoggedIn;

  // Check if the polling configs are valid
  const hasRequiredPollingConfigs =
    !isNullOrUndefined(pollingConfigs?.refreshInterval) &&
    !isNullOrUndefined(pollingConfigs?.jitter);

  const shouldForcePolling =
    !isNullOrUndefined(forcePolling) && forcePolling?.polling;
  // Enable polling either when pusher is offline OR force polling is enabled
  const pollingEnabled = !!(
    isLoggedIn &&
    (!isOnline || shouldForcePolling) &&
    pollingConfigs?.polling &&
    hasRequiredPollingConfigs
  );

  // Determine polling interval based on force polling setting
  // Uses a fixed interval if force polling is enabled, otherwise uses the configured interval
  const refreshInterval = forcePolling
    ? FORCE_POLLING_INTERVAL_MS
    : pollingConfigs?.refreshInterval;

  // Calculate the actual polling interval with jitter
  const intervalFunction = useCallback(() => {
    // Return undefined to disable polling when polling is disabled
    if (!pollingEnabled) {
      return undefined;
    }

    // Add random jitter to the base interval to distribute polling requests
    return refreshInterval + randomNumberBetween(0, pollingConfigs?.jitter);
  }, [pollingEnabled, refreshInterval, pollingConfigs?.jitter]);

  const { refetchPresences } = usePresencesPolling({
    pollingEnabled,
    intervalFunction,
  });

  const { refetch: refetchCallLogs } = useCallLogs({
    query: searchQuery,
    enabled: pollingEnabled,
    refetchInterval: intervalFunction,
  });

  const {
    refetchConversations: refetchConversationsV2,
    refetchConversation: refetchConversationV2,
  } = useConversationsPollingV2({
    pollingEnabled: pollingEnabled,
    intervalFunction,
  });

  const { refetchMessages } = useMessagesPolling({
    pollingEnabled,
    intervalFunction,
  });

  /**
   * `false` by default, and `true` if polling was previously active.
   * If pusher comes online again, it turns back to `false`.
   */
  const pollingWasActive = useRef(false);
  const lastHeartbeatTime = useRef<number>(Date.now());

  /**
   * Checks if error is a 401 Unauthorized (token expired)
   */
  const isAuthError = useCallback((error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false;

    const err = error as Record<string, any>;
    const response = err.response as Record<string, any> | undefined;
    const status = err.status as number | undefined;
    const message = (err.message as string | undefined)?.toLowerCase() || '';

    return (
      response?.status === 401 ||
      status === 401 ||
      message.includes('unauthorized')
    );
  }, []);

  /**
   * Determines if an error is network-related and should be retried
   */
  const isNetworkError = useCallback((error: unknown): boolean => {
    if (!error) return false;

    const errorMessage = (
      (error as Error).message || String(error)
    ).toLowerCase();

    const networkErrorPatterns = [
      'network',
      'fetch',
      'connection',
      'timeout',
      'err_network_changed',
      'connection_refused',
      'err_connection_refused',
      'failed to fetch',
      'network request failed',
      'networkerror',
    ];

    return networkErrorPatterns.some((pattern) =>
      errorMessage.includes(pattern)
    );
  }, []);

  /**
   * Refetches all data with retry logic and network stabilization
   */
  const refetchAllDataWithRetry = useCallback(
    async (reason: string) => {
      const logger = getLogger();
      logger.info('PollingProvider: Starting data refetch', { reason });

      if (networkRecoveryTimeout) {
        clearTimeout(networkRecoveryTimeout);
        networkRecoveryTimeout = null;
      }

      // Keep existing messages visible while refreshing in the background
      personStore.loadMessagesSinceNewestMessageForAllConversations();

      await new Promise((resolve) =>
        setTimeout(resolve, NETWORK_STABILIZATION_DELAY_MS)
      );

      const failedOperations: string[] = [];

      suppressErrorNotifications = true;

      const refetchOperations = [
        {
          name: 'CallLogs',
          operation: () => refetchCallLogs(),
        },
        {
          name: 'Presences',
          operation: () => refetchPresences(),
        },
        {
          name: 'Messages',
          operation: () => refetchMessages(),
        },
        {
          name: 'ConversationsV2',
          operation: () => refetchConversationsV2(),
        },
        {
          name: 'ConversationV2',
          operation: () => refetchConversationV2(),
        },
        {
          name: 'Participants',
          operation: async () => {
            await participantStore.reloadParticipantsForAllConversations();
          },
        },
      ];

      await Promise.allSettled(
        refetchOperations.map(({ name, operation }) =>
          retryWithBackoff({
            operation: async () => {
              const result = await operation();
              return result;
            },
            initialDelay: 1000,
            maxDelay: 8000,
            maxAttempts: 5,
            backoffFactor: 2,
            jitter: true,
            timeout: 15000,
            shouldRetry: (error, attempt) => {
              if (isAuthError(error)) {
                return true;
              }

              if (!isNetworkError(error)) {
                logger.warn(
                  `PollingProvider: ${name} failed with non-network error, not retrying`,
                  { error: error.message }
                );
                return false;
              }

              return true;
            },
            onRetry: async (error, attempt, nextDelay) => {
              if (isAuthError(error)) {
                try {
                  await personStore.getRefreshedToken();
                  logger.info(
                    `PollingProvider: ${name} token refreshed successfully before retry`
                  );
                } catch (tokenError) {
                  const error =
                    tokenError instanceof Error
                      ? tokenError
                      : new Error(String(tokenError));
                  logger.error(
                    `PollingProvider: ${name} failed to refresh token`,
                    error
                  );
                }
              }
            },
          }).catch((error: unknown) => {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            logger.error(
              `PollingProvider: ${name} refetch failed after all retries`,
              { error: errorMessage }
            );

            failedOperations.push(name);
          })
        )
      );

      suppressErrorNotifications = false;

      if (failedOperations.length > 0) {
        logger.warn(
          `PollingProvider: ${failedOperations.length} operation(s) failed after all retries`,
          { failedOperations }
        );
      }
    },
    [
      refetchCallLogs,
      refetchPresences,
      refetchMessages,
      refetchConversationsV2,
      refetchConversationV2,
      participantStore,
      personStore,
      isNetworkError,
      isAuthError,
    ]
  );

  useEffect(() => {
    if (pollingEnabled) {
      pollingWasActive.current = true;
    }
  }, [pollingEnabled]);

  /** Used when pusher comes back, to get stuff updated */
  useEffect(() => {
    const shouldRefetch = isOnline && pollingWasActive.current && !forcePolling;

    if (shouldRefetch) {
      void refetchCallLogs();
      void refetchPresences();
      void refetchMessages();

      void refetchConversationsV2();
      void refetchConversationV2();

      pollingWasActive.current = false;
    }
  }, [isOnline, forcePolling]);

  /**
   * NOTE: Theoretical Race Condition
   * ---------------------------------
   * When a computer wakes from sleep, both the 'online' event listener (below)
   * and the heartbeat detection mechanism may trigger simultaneously, causing
   * two overlapping refetchAllDataWithRetry calls.
   *
   * Probability: Low - requires precise timing where both events fire within
   * the same execution window.
   *
   * Current behavior: Both refetch operations will run concurrently, which may
   * result in duplicate API calls but should not cause data corruption.
   *
   * Future improvement: If we observe error notifications during network recovery
   * in production, implement reference counting or a mutex to ensure only one
   * refetch cycle runs at a time.
   */

  /** Handle wake from sleep via network online event */
  useEffect(() => {
    const handleOnline = () => {
      if (!isLoggedIn) return;

      void refetchAllDataWithRetry('Network came online');
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [isLoggedIn, refetchAllDataWithRetry]);

  /** Handle wake from sleep via heartbeat detection */
  useEffect(() => {
    if (!isLoggedIn) return;

    const heartbeatInterval = setInterval(() => {
      const now = Date.now();
      const expectedTime = lastHeartbeatTime.current + HEARTBEAT_INTERVAL_MS;
      const timeDrift = now - expectedTime;

      if (timeDrift > SLEEP_DETECTION_THRESHOLD_MS) {
        void refetchAllDataWithRetry(
          `Detected wake from sleep (time jumped by ${Math.round(
            timeDrift / 1000
          )}s)`
        );
      }

      lastHeartbeatTime.current = now;
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [isLoggedIn, refetchAllDataWithRetry]);

  return <>{children}</>;
};

export default observer(PollingProvider);
